import { Formik, Form, ErrorMessage } from 'formik';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState, useRef } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { Loader } from '@hivebrite/design-system/Loader';
import { useEmbedLy } from '@hivebrite/react-hooks';

import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import { useQuillEditorContext } from 'hb-react/shared/components/DesignSystem/QuillEditor/contexts/QuillEditorContext';
import ErrorFocus from 'hb-react/shared/components/formik/components/ErrorFocus';
import { Text } from 'hb-react/shared/components/formik/components/inputs';
import RequiredExplanation from 'hb-react/shared/components/formik/components/RequiredExplanation';

const types = ['video'];
const filterUrls = (urls) =>
  urls.filter((url) => /^[^.]+$|\.(?!(gif|jpg|jpeg|png)$)([^.]+$)/.test(url));

const AddVideoForm = ({
  iframeHtml,
  setIframeHtml,
  onClose,
  setFieldError,
}) => {
  const [url, setUrl] = useState();
  const urlTextRef = useRef();

  const emb = useEmbedLy(url, {
    apiUrl: `${window.location.origin}${Routes.embed_path()}`,
    // Max chars in description
    chars: 150,
    // Omit images
    filterUrls,
    types,
    autoplay: false,
  });

  const { response, isLoading } = emb;

  useEffect(() => {
    urlTextRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isLoading && response?.html) {
      const template = document.createElement('template');

      template.innerHTML = response.html;
      const iframe = template.content.firstChild;

      iframe.style = `aspect-ratio: ${iframe.width}/${iframe.height}`;
      iframe.height = null;
      setIframeHtml(iframe.outerHTML);
    } else {
      setIframeHtml('');
    }

    if (url && !isLoading && !response?.html) {
      setFieldError('url', s__('QuillEditor|Url must be a video link'));
    }

    if (url && !isLoading && response?.html) {
      setFieldError('url');
    }
  }, [isLoading, response?.html, setFieldError, setIframeHtml, url]);

  const onUrlChanged = debounce((value) => setUrl(value), 300);

  return (
    <Form style={{ width: '100%' }}>
      <RequiredExplanation mt={0} mb={4} />

      <Text
        ref={urlTextRef}
        name="url"
        label={s__('QuillEditor|URL (https://www.example.com)')}
        placeholder={s__('QuillEditor|URL (https://www.example.com)')}
        validate={{
          required: true,
          urlFormat: true,
        }}
        onChange={onUrlChanged}
      />
      <ErrorMessage name="url">
        {s__('QuillEditor|Url must be a video link')}
      </ErrorMessage>
      <Box>
        {isLoading && <Loader size={40} />}
        {url && !isLoading && iframeHtml && (
          <Box dangerouslySetInnerHTML={{ __html: iframeHtml }} />
        )}
      </Box>
      <Box justifyContent="flex-end" mt={7}>
        <Button variant="secondary" onClick={onClose}>
          {__('Cancel')}
        </Button>
        <Button
          type="submit"
          ml={3}
          isDisabled={!url || !response?.html}
          isLoading={isLoading}
        >
          {s__('QuillEditor|Insert')}
        </Button>
      </Box>
      <ErrorFocus />
    </Form>
  );
};

const AddVideoModal = ({ isOpen, onClose }) => {
  const { quillRef, setDisplayVideoModal, onAddVideo } =
    useQuillEditorContext();

  // Store the "original" selection in a ref. As the modal opens the editor will lose
  // focus and we will lose the original selection so we need to store it.
  const selectionRef = useRef(quillRef.current?.getSelection());
  const selection = selectionRef.current;

  const [iframeHtml, setIframeHtml] = useState();

  const handleSubmit = useCallback(() => {
    quillRef.current.focus();

    quillRef.current.insertEmbed(
      selection?.index,
      'embedly',
      iframeHtml,
      'user',
    );

    onAddVideo && onAddVideo(iframeHtml);

    setDisplayVideoModal(false);
  }, [
    iframeHtml,
    onAddVideo,
    quillRef,
    selection?.index,
    setDisplayVideoModal,
  ]);

  return (
    <ModalWithTemplate
      header={s__('QuillEditor|Insert video')}
      isOpen={isOpen}
      onCloseModal={onClose}
      screenReadersCloseButtonText={s__(
        'QuillEditorAccessibility|Close add video window',
      )}
      width="sm"
      isSticky
    >
      <Formik initialValues={{ url: '' }} onSubmit={handleSubmit}>
        {({ setFieldError }) => (
          <AddVideoForm
            setIframeHtml={setIframeHtml}
            iframeHtml={iframeHtml}
            onClose={onClose}
            setFieldError={setFieldError}
          />
        )}
      </Formik>
    </ModalWithTemplate>
  );
};

export default AddVideoModal;
